import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { apiGet, apiPost } from "@/lib/api";
import { toast } from "sonner";
import { Crown, Check, ShieldCheck, Sparkles, AlertCircle, Loader2 } from "lucide-react";

export default function Premium() {
    const { user, refreshNotifications } = useApp();
    const loc = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [pkg, setPkg] = useState(null);
    const [status, setStatus] = useState(null); // paid | pending | error | cancelled

    const isPremium = user?.tier === "premium";
    const qs = new URLSearchParams(loc.search);
    const sessionId = qs.get("session_id");
    const cancelled = qs.get("cancelled");

    useEffect(() => {
        apiGet("/payments/packages").then(d => setPkg((d.packages || [])[0])).catch(() => {});
    }, []);

    useEffect(() => {
        if (cancelled) {
            setStatus("cancelled");
            toast.message("Checkout cancelled. No charge was made.");
        }
    }, [cancelled]);

    useEffect(() => {
        if (!sessionId) return;
        let attempts = 0;
        let cancelled = false;
        const poll = async () => {
            if (cancelled || attempts >= 8) {
                if (!cancelled && attempts >= 8) { setStatus("pending"); toast.message("Still processing — check notifications in a minute."); }
                return;
            }
            attempts++;
            try {
                const d = await apiGet(`/payments/status/${sessionId}`);
                if (d.payment_status === "paid") {
                    setStatus("paid");
                    toast.success("Payment confirmed — welcome to Owed Premium.");
                    await refreshNotifications();
                    // soft-refresh user tier
                    try { const me = await apiGet("/auth/me"); if (me?.tier === "premium") window.location.href = `${window.location.origin}/app/premium`; } catch { /* ignore */ }
                    return;
                }
                if (d.status === "expired") { setStatus("error"); toast.error("Session expired. Try again."); return; }
                setStatus("pending");
                setTimeout(poll, 2000);
            } catch (e) {
                setStatus("error");
                toast.error("Couldn't verify status — try refreshing.");
            }
        };
        poll();
        return () => { cancelled = true; };
        // eslint-disable-next-line
    }, [sessionId]);

    const handleUpgrade = async () => {
        setLoading(true);
        try {
            const d = await apiPost("/payments/checkout", { package_id: "premium_lifetime", origin_url: window.location.origin });
            if (d.url) window.location.href = d.url;
            else toast.error("No checkout URL returned.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't start checkout");
            setLoading(false);
        }
    };

    return (
        <div className="px-5 sm:px-8 py-10 max-w-4xl mx-auto">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Premium</p>
            <h1 className="mt-2 font-serif text-4xl sm:text-5xl leading-tight">One price. One time. Forever.</h1>
            <p className="mt-3 text-muted-foreground max-w-2xl">We capped premium at <span className="font-semibold text-foreground">$5 — lifetime</span>. No subscriptions, no upsells. Pay once and Owed AI prepares every packet, ever.</p>

            {sessionId && status === "pending" && (
                <Card className="mt-6 p-5 flex items-center gap-3 border-warning/40 bg-warning/5" data-testid="premium-status-pending">
                    <Loader2 className="h-5 w-5 animate-spin text-warning" />
                    <p className="text-sm">Verifying your payment with Stripe…</p>
                </Card>
            )}
            {status === "paid" && (
                <Card className="mt-6 p-5 flex items-center gap-3 border-success/40 bg-success/5" data-testid="premium-status-paid">
                    <Check className="h-5 w-5 text-success" />
                    <p className="text-sm">Payment confirmed — Owed Premium is active on your account.</p>
                </Card>
            )}
            {status === "error" && (
                <Card className="mt-6 p-5 flex items-center gap-3 border-destructive/40 bg-destructive/5" data-testid="premium-status-error">
                    <AlertCircle className="h-5 w-5 text-destructive" />
                    <p className="text-sm">Something went wrong. Reload, or contact support if you were charged.</p>
                </Card>
            )}

            <div className="mt-8 grid md:grid-cols-2 gap-6">
                <Card className="p-8 border-border relative overflow-hidden">
                    <div aria-hidden className="absolute -top-10 -right-10 h-28 w-28 rounded-full bg-primary/10 blur-xl" />
                    <Crown className="h-6 w-6 text-accent-foreground" />
                    <h2 className="mt-4 font-serif text-3xl">Free</h2>
                    <p className="text-sm text-muted-foreground mt-1">Everything to find what's owed to you.</p>
                    <ul className="mt-6 space-y-2.5 text-sm">
                        {["Full grants + unclaimed scan", "Curated grant catalog & matching", "Live Grants.gov feed", "1 AI-prepared packet / month", "Manual profile vault"].map(f => (
                            <li key={f} className="flex items-start gap-2"><Check className="h-4 w-4 text-success shrink-0 mt-0.5" /> <span>{f}</span></li>
                        ))}
                    </ul>
                </Card>

                <Card className="p-8 border-2 border-accent bg-gradient-warm relative overflow-hidden shadow-elegant" data-testid="premium-plan-card">
                    <div aria-hidden className="absolute -top-10 -right-10 h-28 w-28 rounded-full bg-accent/20 blur-xl" />
                    <div className="flex items-center gap-2">
                        <Sparkles className="h-6 w-6 text-accent-foreground" />
                        <span className="text-xs font-mono uppercase tracking-[0.18em] text-accent-foreground bg-accent/20 rounded-full px-2 py-0.5">recommended</span>
                    </div>
                    <h2 className="mt-4 font-serif text-3xl">Premium</h2>
                    <div className="mt-1 flex items-baseline gap-2">
                        <p className="font-serif text-5xl tracking-tight">$5</p>
                        <p className="text-sm text-muted-foreground">one-time · lifetime</p>
                    </div>
                    <ul className="mt-6 space-y-2.5 text-sm">
                        {["Unlimited AI-prepared packets", "Priority matching & deep narratives", "Unlimited signed PDFs + vault", "Cross-state unclaimed property scans", "Priority application processing"].map(f => (
                            <li key={f} className="flex items-start gap-2"><Check className="h-4 w-4 text-success shrink-0 mt-0.5" /> <span>{f}</span></li>
                        ))}
                    </ul>

                    {isPremium ? (
                        <div className="mt-6 p-3 rounded-xl bg-success/10 text-success text-sm inline-flex items-center gap-2" data-testid="premium-already-active">
                            <Check className="h-4 w-4" /> You're on Premium. Thank you.
                        </div>
                    ) : (
                        <Button onClick={handleUpgrade} disabled={loading || !pkg} className="mt-6 w-full rounded-full h-12 bg-foreground text-background hover:bg-foreground/90" data-testid="premium-upgrade-btn">
                            {loading ? "Redirecting to Stripe…" : "Upgrade for $5"}
                        </Button>
                    )}

                    <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                        <ShieldCheck className="h-3.5 w-3.5" /> Secure Stripe checkout. Never more than $5.
                    </div>
                </Card>
            </div>

            <p className="mt-10 text-center text-xs text-muted-foreground">
                Owed will never charge more than $5, ever. If Stripe returns anything higher, the server rejects it.
            </p>
        </div>
    );
}
